import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    petType: '',
    rating: ''
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services`, {
          params: {
            search: searchParams.get('search'),
            ...filters
          }
        });
        setServices(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching services:', error);
        setLoading(false);
      }
    };

    fetchServices();
  }, [searchParams, filters]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Pet Services</h2>
        <div className="flex gap-4 flex-wrap">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="p-2 border rounded-lg"
          >
            <option value="">All Categories</option>
            <option value="grooming">Grooming</option>
            <option value="veterinarian">Veterinary</option>
            <option value="training">Training</option>
            <option value="sitting">Pet Sitting</option>
            <option value="emergency">Emergency Care</option>
            <option value="supplies">Pet Supplies</option>
          </select>
          <select
            value={filters.petType}
            onChange={(e) => handleFilterChange('petType', e.target.value)}
            className="p-2 border rounded-lg"
          >
            <option value="">All Pets</option>
            <option value="dog">Dogs</option>
            <option value="cat">Cats</option>
            {/* <option value="bird">Birds</option>
            <option value="reptile">Reptiles</option>
            <option value="small_animal">Small Animals</option> */}
          </select>
          <select
            value={filters.rating}
            onChange={(e) => handleFilterChange('rating', e.target.value)}
            className="p-2 border rounded-lg"
          >
            <option value="">All Ratings</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <Link
            key={service.id}
            to={`/services/${service.id}`}
            className="block bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="p-6 relative">
              {service.isEmergencyService && (
                <span className="absolute top-4 right-4 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                  24/7 Emergency
                </span>
              )}
              <div className="mb-4">
                <img 
                  src={service.image_url || `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`} 
                  alt={service.business_name || 'Pet Service'}
                  className="w-full h-48 object-cover rounded-lg bg-blue-50"
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`;
                  }}
                />
              </div>
              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{service.business_name}</h3>
                  <p className="text-blue-600 font-medium">{service.business_name}</p>
                </div>
                
                <div className="flex items-center">
                  <span className="text-yellow-400 text-lg">{'★'.repeat(Math.floor(service.rating))}</span>
                  <span className="text-gray-200 text-lg">{'★'.repeat(5 - Math.floor(service.rating))}</span>
                  {service.reviews_count > 0 && (
                    <span className="text-gray-600 ml-2 text-sm">({service.reviews_count} reviews)</span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <p className="text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {service.address.street}
                  </p>
                  <p className="text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {service.contact.phone}
                  </p>
                </div>

                <div className="pt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-blue-50 text-blue-700">
                    {service.serviceType}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default ServiceList; 